// packages/schema/src/dealer-addons.ts
/**
 * Dealer add-ons schema — site-wide catalog of fees, warranties,
 * protection packages, accessories, etc. applied per-VDP at render time.
 * See docs/specs/generic-feed-pipeline.md §4 (data model) and §12 (resolver semantics).
 *
 * Feed syncs never touch this table or `vehicles.dealerAddonOverrides`.
 */
import { z } from "zod";
import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sites } from "./tables";

export const addonCategoryEnum = pgEnum("addon_category", [
  "fee",
  "warranty",
  "protection",
  "accessory",
  "package",
  "service",
]);
export const addonScopeEnum = pgEnum("addon_scope", ["mandatory", "optional"]);

export type AppliesToRule = {
  condition?: ("new" | "used" | "certified-pre-owned")[];
  bodyStyle?: string[];
  fuelType?: string[];
  minYear?: number;
  maxYear?: number;
  minPriceCents?: number;
  maxPriceCents?: number;
};

export type DealerAddonOverrides = {
  excludes: string[];
  includes: Array<{
    name: string;
    description: string | null;
    priceCents: number;
    category: "fee" | "warranty" | "protection" | "accessory" | "package" | "service";
    scope: "mandatory" | "optional";
  }>;
};

export const dealerAddons = pgTable(
  "dealer_addons",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    priceCents: integer("price_cents").notNull(),
    category: addonCategoryEnum("category").notNull(),
    scope: addonScopeEnum("scope").notNull(),
    appliesTo: jsonb("applies_to").$type<AppliesToRule>(),
    displayOrder: integer("display_order").notNull().default(0),
    enabled: boolean("enabled").notNull().default(true),
    imageUrl: text("image_url"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteNameUnique: uniqueIndex("dealer_addons_site_name_idx").on(t.siteId, t.name),
    siteEnabledOrderIdx: index("dealer_addons_site_enabled_order_idx").on(
      t.siteId,
      t.enabled,
      t.displayOrder,
    ),
  }),
);

export type DealerAddon = typeof dealerAddons.$inferSelect;
export type NewDealerAddon = typeof dealerAddons.$inferInsert;

export const DealerAddonOverridesSchema = z.object({
  excludes: z.array(z.string().uuid()).default([]),
  includes: z
    .array(
      z.object({
        name: z.string(),
        description: z.string().nullable(),
        priceCents: z.number().int(),
        category: z.enum(["fee", "warranty", "protection", "accessory", "package", "service"]),
        scope: z.enum(["mandatory", "optional"]),
      }),
    )
    .default([]),
});
